import { X } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type RefObject,
  forwardRef,
  useEffect,
  useRef,
} from "react";

import { useMotion } from "./MotionProvider";
import type { MotionPreference } from "./motion";

export const cx = (
  ...values: Array<string | false | null | undefined>
): string => values.filter(Boolean).join(" ");

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "destructive";
  busy?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      busy = false,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      className={cx("button", `button--${variant}`, className)}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      {...props}
    >
      {busy ? <span className="button__busy-dot" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  ),
);

Button.displayName = "Button";

type ButtonLinkProps = ComponentPropsWithoutRef<"a"> & {
  variant?: "primary" | "secondary" | "ghost";
};

export const ButtonLink = ({
  variant = "primary",
  className,
  children,
  ...props
}: ButtonLinkProps) => (
  <a
    className={cx("button", `button--${variant}`, className)}
    {...props}
  >
    {children}
  </a>
);

type PanelProps = ComponentPropsWithoutRef<"section"> & {
  variant?: "default" | "soft" | "raised";
};

export const Panel = ({
  variant = "default",
  className,
  children,
  ...props
}: PanelProps) => (
  <section className={cx("panel", `panel--${variant}`, className)} {...props}>
    {children}
  </section>
);

export const Workbench = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"section">) => (
  <section
    className={cx("workbench", "theme-workbench", className)}
    {...props}
  >
    {children}
  </section>
);

type StatusPillProps = PropsWithChildren<{
  tone?: "neutral" | "info" | "success" | "warning" | "error";
  pulse?: boolean;
}>;

export const StatusPill = ({
  tone = "neutral",
  pulse = false,
  children,
}: StatusPillProps) => (
  <span className={cx("status-pill", `status-pill--${tone}`)}>
    <span
      className={cx("status-pill__dot", pulse && "status-pill__dot--pulse")}
      aria-hidden="true"
    />
    <span>{children}</span>
  </span>
);

type InlineNoticeProps = PropsWithChildren<{
  tone?: "info" | "warning" | "error" | "success";
  title: string;
}>;

export const InlineNotice = ({
  tone = "info",
  title,
  children,
}: InlineNoticeProps) => (
  <div className={cx("inline-notice", `inline-notice--${tone}`)}>
    <span className="inline-notice__mark" aria-hidden="true" />
    <div>
      <strong>{title}</strong>
      <div className="inline-notice__body">{children}</div>
    </div>
  </div>
);

export type MetadataItem = {
  label: string;
  value: string;
  mono?: boolean;
};

export const MetadataStrip = ({ items }: { items: MetadataItem[] }) => (
  <dl className="metadata-strip">
    {items.map((item) => (
      <div className="metadata-strip__item" key={item.label}>
        <dt>{item.label}</dt>
        <dd className={item.mono ? "is-mono" : undefined}>{item.value}</dd>
      </div>
    ))}
  </dl>
);

const motionOptions: Array<{
  value: MotionPreference;
  label: string;
}> = [
  { value: "system", label: "Sistem" },
  { value: "active", label: "Active" },
  { value: "reduced", label: "Reduse" },
];

export const MotionSelector = () => {
  const { preference, resolved, systemReduced, setPreference } = useMotion();

  return (
    <div className="motion-control">
      <span className="motion-control__label" id="motion-label">
        Mișcare
      </span>
      <div
        className="segmented-control"
        role="group"
        aria-labelledby="motion-label"
      >
        {motionOptions.map((option) => {
          const disabled = option.value === "active" && systemReduced;
          return (
            <button
              key={option.value}
              type="button"
              className="segmented-control__button"
              aria-pressed={preference === option.value}
              disabled={disabled}
              title={
                disabled
                  ? "Sistemul de operare cere mișcare redusă."
                  : undefined
              }
              onClick={() => setPreference(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <span className="sr-only" aria-live="polite">
        Mișcare efectivă: {resolved === "reduced" ? "redusă" : "activă"}.
      </span>
    </div>
  );
};

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

type DrawerProps = PropsWithChildren<{
  open: boolean;
  title: string;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLElement | null>;
}>;

export const Drawer = ({
  open,
  title,
  onClose,
  returnFocusRef,
  children,
}: DrawerProps) => {
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const drawer = drawerRef.current;
    const focusable = Array.from(
      drawer?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    focusable[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("has-open-drawer");
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("has-open-drawer");
      returnFocusRef.current?.focus();
    };
  }, [onClose, open, returnFocusRef]);

  if (!open) {
    return null;
  }

  return (
    <div className="drawer-layer">
      <div
        className="drawer-backdrop"
        aria-hidden="true"
        onMouseDown={onClose}
      />
      <aside
        ref={drawerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <div className="drawer__header">
          <h2 id="drawer-title">{title}</h2>
          <Button
            variant="ghost"
            className="button--icon"
            aria-label="Închide sesiunile"
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </Button>
        </div>
        {children}
      </aside>
    </div>
  );
};
